import { Deferred, Head, router, usePage } from "@inertiajs/react";
import type { ColumnDef } from "@tanstack/react-table";
import { type ContextFn, format, type Locale } from "date-fns";
import type { TFunction } from "i18next";
import {
	ArrowLeftRightIcon,
	BracesIcon,
	CopyIcon,
	ExpandIcon,
	EyeIcon,
	EyeOffIcon,
	HistoryIcon,
	Loader2,
	MoreHorizontal,
	MousePointerClick,
	PencilIcon,
	PlugZapIcon,
	PlusIcon,
	RefreshCwIcon,
	SearchIcon,
	Trash2Icon,
	TriangleAlertIcon,
	XIcon,
} from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ApptPagination from "@/components/admin-portal/appointment/pagination-list";
import { PageContainer } from "@/components/admin-portal/shared/page-layout";
import { useDateContext } from "@/components/providers/date-provider";
import {
	ResponsiveDialog,
	type ResponsiveDialogProps,
} from "@/components/shared/responsive-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { DataTableColumnHeader } from "@/components/ui/data-table/column-header";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useToast } from "@/hooks/use-toast";
import { getCsrfToken } from "@/lib/csrf";
import { cn, copyToClipboard, populateQueryParams } from "@/lib/utils";
import type {
	GenericContent,
	GenericContentAuditEntry,
	GenericContentCodeReference,
	GenericContentFilterOptions,
} from "@/types/admin-portal/generic-content";
import type { GlobalPageProps as BaseGlobalPageProps } from "@/types/globals";
import type { Metadata } from "@/types/pagy";

export interface PageProps {
	/** deferred — undefined on the first paint, then filled by the partial reload */
	contents?: {
		data: GenericContent[];
		metadata: Metadata;
	};
	filterOptions: GenericContentFilterOptions;
	/** The (group, key) pairs backend code reads, and whether a row backs them. */
	codeReferences: GenericContentCodeReference[];
}

export interface GenericContentIndexGlobalPageProps
	extends BaseGlobalPageProps,
		PageProps {
	[key: string]: unknown;
}

type FormValues = {
	groupKey: string;
	contentKey: string;
	contentValue: string;
	isActive: boolean;
};

type FormPrefill = {
	groupKey: string;
	contentKey: string;
};

/**
 * A destructive action waiting on confirmation. `deactivate` is only ever
 * queued for a row backing a code lookup — switching off free-form content
 * stays a single click.
 */
type PendingAction = {
	kind: "delete" | "deactivate";
	content: GenericContent;
};

/**
 * The history dialog's whole lifecycle. `status` distinguishes the three states
 * the fetch can leave it in — an empty list on a row nobody has touched since
 * the audit table landed is a real answer, not a failure, and saying so needs
 * more than `entries.length === 0`.
 */
type HistoryDialogState = {
	content: GenericContent;
	status: "loading" | "ready" | "error";
	entries: GenericContentAuditEntry[];
	/** Set when the server returned only the newest `limit` of a longer trail. */
	truncated: boolean;
	limit: number;
};

type FormDialogState = {
	mode: "create" | "edit";
	content: GenericContent | null;
	/** Seeds a create form, so a lookup with no row can be filled in as named. */
	prefill?: FormPrefill;
};

/**
 * What the value viewer is showing, captured when it opened.
 *
 * The value is carried rather than looked up again: for a credential row it
 * exists only in the reveal cache, and the row it came from may leave the
 * current page of the list while the dialog is open.
 */
type ValueDialogState = {
	content: GenericContent;
	value: string;
};

/** Query params the controller echoes back after a mutation. */
const PRESERVED_FILTER_KEYS = ["q", "group_key", "status", "page", "limit"];

const ALL_OPTION = "all";

/** Props reloaded on every filter/pagination visit — keeps the payload small. */
const RELOAD_ONLY = [
	"contents",
	"filterOptions",
	"codeReferences",
	"adminPortal",
	"flash",
	"errors",
];

export default function Index({
	contents,
	filterOptions,
	codeReferences,
}: PageProps) {
	const { props: globalProps, url: pageURL } =
		usePage<GenericContentIndexGlobalPageProps>();
	const { locale, tzDate } = useDateContext();
	const { toast } = useToast();
	const { t } = useTranslation("generic-contents");

	const indexRoute =
		globalProps.adminPortal?.router?.adminPortal?.genericContents?.index;
	const toggleRoute =
		globalProps.adminPortal?.router?.adminPortal?.genericContents?.toggle;
	const repairKeysRoute =
		globalProps.adminPortal?.router?.adminPortal?.genericContents?.repairKeys;
	const revealRoute =
		globalProps.adminPortal?.router?.adminPortal?.genericContents?.reveal;
	const historyRoute =
		globalProps.adminPortal?.router?.adminPortal?.genericContents?.history;
	const currentQuery = globalProps.adminPortal?.currentQuery;

	const contentsData = useMemo(
		() => (Array.isArray(contents?.data) ? contents.data : []),
		[contents?.data],
	);

	// `current_query` is camelised alongside the rest of the adminPortal prop,
	// so `?group_key=` comes back as `groupKey`.
	const currentGroupKey = currentQuery?.groupKey || "";
	const currentStatus = currentQuery?.status || "";
	const currentSearch = currentQuery?.q || "";

	const [searchTerm, setSearchTerm] = useState(currentSearch);
	const debouncedSearch = useDebouncedValue(searchTerm, 350);
	// A sensitive row arrives with `contentValue: null` — the server keeps
	// credentials out of the page payload entirely. Whatever the admin has
	// explicitly revealed is cached here for the lifetime of the page.
	const [revealedValues, setRevealedValues] = useState<Record<string, string>>(
		{},
	);
	const [pendingReveals, setPendingReveals] = useState<string[]>([]);
	// The create/edit dialog deliberately lives in React state rather than in the
	// URL: a validation failure comes back as a redirect, and a URL-driven dialog
	// would unmount while the deferred `contents` prop is in flight — throwing
	// away whatever the admin had just typed.
	const [formDialog, setFormDialog] = useState<FormDialogState | null>(null);
	// Both destructive actions confirm through the same dialog, because they have
	// the same consequence for a row a service reads: the lookup stops resolving
	// and the caller falls back without anything failing.
	const [pendingAction, setPendingAction] = useState<PendingAction | null>(
		null,
	);
	const [isActionLoading, setIsActionLoading] = useState(false);
	// What the admin has typed to confirm deleting a credential row. The server
	// enforces the match; this only keeps the button from being reachable by
	// reflex, which is the failure mode it exists for.
	const [deleteConfirmation, setDeleteConfirmation] = useState("");
	// Read on demand rather than shipped with the list: a row's trail is
	// unbounded and nobody looks at it while scanning the table.
	const [historyDialog, setHistoryDialog] = useState<HistoryDialogState | null>(
		null,
	);
	// One row in the live table holds 2.7 KB of JSON across 71 lines. The cell
	// summarises it; this is where it can actually be read.
	const [valueDialog, setValueDialog] = useState<ValueDialogState | null>(null);

	const visit = useCallback(
		(baseUrl: string, params: Record<string, string | null>) => {
			const { fullUrl, queryParams } = populateQueryParams(baseUrl, params);

			router.get(fullUrl, queryParams, {
				only: RELOAD_ONLY,
				preserveScroll: true,
				preserveState: true,
			});
		},
		[],
	);

	// Any filter change resets to page 1 — staying on page 7 of a narrower result
	// set would render an empty table.
	const applyFilter = useCallback(
		(params: Record<string, string | null>) => {
			if (!indexRoute) return;
			visit(indexRoute, { ...params, page: null });
		},
		[indexRoute, visit],
	);

	useEffect(() => {
		if (debouncedSearch === currentSearch) return;
		applyFilter({
			q: debouncedSearch || null,
			group_key: currentGroupKey || null,
			status: currentStatus || null,
		});
	}, [
		debouncedSearch,
		currentSearch,
		currentGroupKey,
		currentStatus,
		applyFilter,
	]);

	const handleGroupKeyChange = useCallback(
		(value: string) => {
			applyFilter({
				group_key: value === ALL_OPTION ? null : value,
				q: currentSearch || null,
				status: currentStatus || null,
			});
		},
		[applyFilter, currentSearch, currentStatus],
	);

	const handleStatusChange = useCallback(
		(value: string) => {
			applyFilter({
				status: value === ALL_OPTION ? null : value,
				q: currentSearch || null,
				group_key: currentGroupKey || null,
			});
		},
		[applyFilter, currentSearch, currentGroupKey],
	);

	const hasActiveFilters = Boolean(
		currentSearch || currentGroupKey || currentStatus,
	);

	// Lookups the backend performs that no active row satisfies: the caller is
	// quietly using its hardcoded fallback, and the table alone cannot show that
	// — a row named the other way round looks just as correct as the real one.
	const unbackedReferences = useMemo(
		() => (codeReferences ?? []).filter((ref) => ref.status !== "ACTIVE"),
		[codeReferences],
	);

	// Mutations target a bare REST path, so the active filters have to ride along
	// in the query string: the controller rebuilds its redirect target from
	// `request.query_parameters`, and without this every create/update/delete
	// would dump the admin back on an unfiltered page 1.
	const filterQuery = useMemo(() => {
		const current = new URLSearchParams(pageURL.split("?")[1] || "");
		const preserved = new URLSearchParams();

		for (const key of PRESERVED_FILTER_KEYS) {
			const value = current.get(key);
			if (value) preserved.set(key, value);
		}

		const query = preserved.toString();
		return query ? `?${query}` : "";
	}, [pageURL]);

	const handleClearFilters = useCallback(() => {
		setSearchTerm("");
		applyFilter({ q: null, group_key: null, status: null });
	}, [applyFilter]);

	const changePage = useCallback(
		(
			type: "prev" | "next" | "first" | "last" | "limit" | "page",
			value?: string,
		) => {
			const metadata = contents?.metadata;
			if (!metadata) return;

			let url: string | undefined;
			switch (type) {
				case "prev":
					url = metadata.prevUrl;
					break;
				case "next":
					url = metadata.nextUrl;
					break;
				case "first":
					url = metadata.firstUrl;
					break;
				case "last":
					url = metadata.lastUrl;
					break;
				case "limit": {
					if (!value) return;
					// Widening the page size while on page 7 would ask for a page that
					// no longer exists — the server recovers from that, but the admin
					// still expects to see the start of the resized list.
					const { fullUrl } = populateQueryParams(metadata.pageUrl, {
						limit: value,
						page: null,
					});
					url = fullUrl;
					break;
				}
				case "page": {
					if (!value) return;
					const { fullUrl } = populateQueryParams(metadata.pageUrl, {
						page: value,
					});
					url = fullUrl;
					break;
				}
			}

			if (!url) return;

			router.get(
				url,
				{},
				{ only: RELOAD_ONLY, preserveScroll: false, preserveState: true },
			);
		},
		[contents?.metadata],
	);

	/**
	 * Returns the row's value, fetching it from the reveal endpoint the first
	 * time a sensitive row needs it. `null` means the value could not be
	 * obtained — the caller should abort rather than act on an empty string.
	 */
	const resolveValue = useCallback(
		async (
			content: GenericContent,
			options?: { force?: boolean },
		): Promise<string | null> => {
			if (!content.isSensitive) return content.contentValue ?? "";

			const cached = revealedValues[content.id];
			// `force` is for a row that has since changed: what was revealed
			// belongs to the revision that was replaced, so re-seeding a form from
			// it would put a rotated credential's old value back on the wire.
			if (!options?.force && typeof cached === "string") return cached;
			if (!revealRoute) return null;

			setPendingReveals((prev) => [...prev, content.id]);
			try {
				// POST: each reveal of a secret files an audit entry, which a GET would let
				// any site plant through a signed-in admin's browser.
				const response = await fetch(revealRoute.replace(":id", content.id), {
					method: "POST",
					headers: {
						Accept: "application/json",
						"X-CSRF-Token": getCsrfToken(),
					},
				});
				if (!response.ok) throw new Error(`HTTP ${response.status}`);

				const data = (await response.json()) as { contentValue: string | null };
				const value = data.contentValue ?? "";
				setRevealedValues((prev) => ({ ...prev, [content.id]: value }));

				return value;
			} catch {
				toast({
					title: t("toast.reveal_error"),
					description: t("toast.reveal_error_description", {
						key: content.contentKey,
					}),
					variant: "destructive",
				});

				return null;
			} finally {
				setPendingReveals((prev) => prev.filter((id) => id !== content.id));
			}
		},
		[revealRoute, revealedValues, toast, t],
	);

	const handleToggleReveal = useCallback(
		(content: GenericContent) => {
			if (content.id in revealedValues) {
				setRevealedValues((prev) => {
					const { [content.id]: _hidden, ...rest } = prev;
					return rest;
				});
				return;
			}

			void resolveValue(content);
		},
		[revealedValues, resolveValue],
	);

	// Opens the full value in a dialog.
	//
	// Deliberately click-driven and never on hover: a credential's value exists
	// only after `reveal`, and a reveal writes an audit entry. A viewer that
	// opened on hover would file events nobody performed and put a live token on
	// screen because a cursor crossed the table. The trigger is offered only for
	// a row whose value is already on the client, so opening this fetches
	// nothing.
	const handleViewValue = useCallback(
		async (content: GenericContent) => {
			const value = await resolveValue(content);
			if (value === null) return;

			setValueDialog({ content, value });
		},
		[resolveValue],
	);

	// Drops a cached secret once its row has been edited — the value the admin
	// just saved is the one they typed, not the one fetched earlier.
	const forgetRevealedValue = useCallback((contentId: string) => {
		setRevealedValues((prev) => {
			const { [contentId]: _stale, ...rest } = prev;
			return rest;
		});
	}, []);

	// A key is a plain string copy. Unlike a value it can never be a credential,
	// so it needs neither `resolveValue` nor an audit entry — which is also why
	// it is offered on every row without qualification.
	const handleCopyKey = useCallback(
		(content: GenericContent) => {
			copyToClipboard(content.contentKey);
			toast({
				title: t("toast.copy_success"),
				description: t("toast.copy_key_description", {
					key: content.contentKey,
				}),
			});
		},
		[toast, t],
	);

	const handleCopyValue = useCallback(
		async (content: GenericContent) => {
			const value = await resolveValue(content);
			if (value === null) return;

			copyToClipboard(value);
			toast({
				title: t("toast.copy_success"),
				description: t("toast.copy_success_description", {
					key: content.contentKey,
				}),
			});
		},
		[resolveValue, toast, t],
	);

	const handleOpenHistory = useCallback(
		async (content: GenericContent) => {
			if (!historyRoute) return;

			setHistoryDialog({
				content,
				status: "loading",
				entries: [],
				truncated: false,
				limit: 0,
			});

			try {
				const response = await fetch(historyRoute.replace(":id", content.id), {
					headers: { Accept: "application/json" },
				});
				if (!response.ok) throw new Error(`HTTP ${response.status}`);

				const data = (await response.json()) as {
					entries: GenericContentAuditEntry[];
					truncated: boolean;
					limit: number;
				};

				// The dialog may have been closed, or reopened on another row, while
				// this was in flight — dropping a stale response is what keeps one
				// row's trail from appearing under another row's name.
				setHistoryDialog((current) =>
					current?.content.id === content.id
						? {
								...current,
								status: "ready",
								entries: data.entries ?? [],
								truncated: !!data.truncated,
								limit: data.limit ?? 0,
							}
						: current,
				);
			} catch {
				setHistoryDialog((current) =>
					current?.content.id === content.id
						? { ...current, status: "error" }
						: current,
				);
			}
		},
		[historyRoute],
	);

	const submitToggle = useCallback(
		(content: GenericContent, options?: { onFinish?: () => void }) => {
			if (!toggleRoute) return;

			router.post(
				`${toggleRoute.replace(":id", content.id)}${filterQuery}`,
				// The switch flips whatever the row holds *now*, so it has to say
				// which revision it was drawn from: if another admin already flipped
				// it, this click would turn the row back the other way instead.
				{ revision_token: content.revisionToken },
				{
					only: RELOAD_ONLY,
					preserveScroll: true,
					preserveState: true,
					onStart: () => setIsActionLoading(true),
					onFinish: () => {
						setIsActionLoading(false);
						options?.onFinish?.();
					},
				},
			);
		},
		[toggleRoute, filterQuery],
	);

	// Switching off a row a service reads does not stop that service — it hands
	// it the hardcoded fallback, silently. That is worth one confirmation naming
	// the caller and the value about to take effect; every other row toggles on
	// the spot as before.
	const handleToggleActive = useCallback(
		(content: GenericContent) => {
			if (content.isActive && content.codeReference) {
				setIsActionLoading(false);
				setPendingAction({ kind: "deactivate", content });
				return;
			}

			submitToggle(content);
		},
		[submitToggle],
	);

	// Puts a reversed row's keys back in the order the code reads them. The
	// alternative — creating a correctly named row — would leave this one in the
	// table alongside its replacement, active and spelling out the same two
	// words, so the swap is what the page offers.
	const handleRepairKeys = useCallback(
		(contentId: string) => {
			if (!repairKeysRoute) return;

			router.post(
				`${repairKeysRoute.replace(":id", contentId)}${filterQuery}`,
				{},
				{ only: RELOAD_ONLY, preserveScroll: true, preserveState: true },
			);
		},
		[repairKeysRoute, filterQuery],
	);

	// The edit form needs the real value in the textarea, so opening it on a
	// sensitive row is itself a reveal — and is logged server-side as one.
	const handleEdit = useCallback(
		async (content: GenericContent) => {
			const value = await resolveValue(content);
			if (value === null) return;

			setFormDialog({
				mode: "edit",
				content: { ...content, contentValue: value },
			});
		},
		[resolveValue],
	);

	const handleDelete = useCallback((content: GenericContent) => {
		setIsActionLoading(false);
		setDeleteConfirmation("");
		setPendingAction({ kind: "delete", content });
	}, []);

	const handleActionDialogOpenChange = useCallback(
		(open: boolean) => {
			// Closing mid-flight would leave the confirm button's loading state
			// stranded on a dialog nobody can see.
			if (!open && isActionLoading) return;
			if (!open) {
				setPendingAction(null);
				setDeleteConfirmation("");
			}
		},
		[isActionLoading],
	);

	// A credential row is the one thing this page can destroy with nothing left
	// to restore from — the audit line records its value as [REDACTED]. Typing
	// the key is what turns "delete" from a reflex into a decision.
	const needsTypedConfirmation =
		pendingAction?.kind === "delete" && pendingAction.content.isSensitive;
	const isConfirmationSatisfied =
		!needsTypedConfirmation ||
		deleteConfirmation.trim() === pendingAction?.content.contentKey;

	const handleConfirmAction = useCallback(() => {
		if (!pendingAction) return;

		if (pendingAction.kind === "deactivate") {
			submitToggle(pendingAction.content, {
				onFinish: () => setPendingAction(null),
			});
			return;
		}

		if (!indexRoute) return;

		router.delete(`${indexRoute}/${pendingAction.content.id}${filterQuery}`, {
			only: RELOAD_ONLY,
			preserveScroll: true,
			preserveState: true,
			data: { confirm_key: deleteConfirmation.trim() },
			onStart: () => setIsActionLoading(true),
			onFinish: () => {
				setIsActionLoading(false);
				setDeleteConfirmation("");
				setPendingAction(null);
			},
		});
	}, [
		pendingAction,
		indexRoute,
		filterQuery,
		submitToggle,
		deleteConfirmation,
	]);

	const columns = useMemo<ColumnDef<GenericContent>[]>(
		() =>
			getColumns({
				t,
				locale,
				tzDate,
				revealedValues,
				pendingReveals,
				onToggleReveal: handleToggleReveal,
				onViewValue: handleViewValue,
				onCopyKey: handleCopyKey,
				onCopyValue: handleCopyValue,
				onToggleActive: handleToggleActive,
				onEdit: handleEdit,
				onRepairKeys: handleRepairKeys,
				onDelete: handleDelete,
				onOpenHistory: handleOpenHistory,
			}),
		[
			t,
			locale,
			tzDate,
			revealedValues,
			pendingReveals,
			handleToggleReveal,
			handleViewValue,
			handleCopyKey,
			handleCopyValue,
			handleToggleActive,
			handleEdit,
			handleRepairKeys,
			handleDelete,
			handleOpenHistory,
		],
	);

	// The revision that replaced the one the open edit dialog was seeded from.
	//
	// The server refuses a save aimed at a superseded revision, and that refusal
	// redirects back through the list — so the newer revision is already on the
	// page by the time the error is on screen. Without this comparison the
	// refusal is a dead end: the dialog keeps the token it opened with, so
	// pressing Save again sends the same stale one and is refused forever, and
	// nothing tells the admin that closing and reopening is the way out.
	const supersededBy = useMemo(() => {
		const editing = formDialog?.mode === "edit" ? formDialog.content : null;
		if (!editing) return null;

		const current = contentsData.find((row) => row.id === editing.id);
		if (!current || current.revisionToken === editing.revisionToken)
			return null;

		return current;
	}, [formDialog, contentsData]);

	// Re-seeds the form from that newer revision. Explicit rather than automatic:
	// what the admin typed is theirs until they choose to drop it.
	const reloadFormFromLatest = useCallback(async () => {
		if (!supersededBy) return;

		const value = await resolveValue(supersededBy, { force: true });
		if (value === null) return;

		setFormDialog({
			mode: "edit",
			content: { ...supersededBy, contentValue: value },
		});
	}, [supersededBy, resolveValue]);

	const closeFormDialog = useCallback(() => setFormDialog(null), []);

	const handleFormSaved = useCallback(
		(contentId?: string) => {
			if (contentId) forgetRevealedValue(contentId);
			closeFormDialog();
		},
		[forgetRevealedValue, closeFormDialog],
	);

	const historyDialogProps = useMemo<ResponsiveDialogProps>(
		() => ({
			title: t("modal.history.title"),
			description: historyDialog
				? t("modal.history.description", {
						group: historyDialog.content.groupKey,
						key: historyDialog.content.contentKey,
					})
				: "",
			isOpen: !!historyDialog,
			dialogWidth: "640px",
			onOpenChange: (open: boolean) => {
				if (!open) setHistoryDialog(null);
			},
		}),
		[historyDialog, t],
	);

	const valueDialogProps = useMemo<ResponsiveDialogProps>(
		() => ({
			title: t("modal.value.title"),
			description: valueDialog
				? t("modal.value.description", {
						group: valueDialog.content.groupKey,
						key: valueDialog.content.contentKey,
					})
				: "",
			isOpen: !!valueDialog,
			dialogWidth: "720px",
			onOpenChange: (open: boolean) => {
				if (!open) setValueDialog(null);
			},
		}),
		[valueDialog, t],
	);

	const formDialogProps = useMemo<ResponsiveDialogProps>(() => {
		const isEditMode = formDialog?.mode === "edit";

		return {
			title: isEditMode ? t("modal.update.title") : t("modal.add.title"),
			description: isEditMode
				? t("modal.update.description")
				: t("modal.add.description"),
			isOpen: !!formDialog,
			dialogWidth: "560px",
			onOpenChange: closeFormDialog,
		};
	}, [formDialog, closeFormDialog, t]);

	return (
		<>
			<Head title={t("head_title")} />

			<PageContainer className="min-h-[100vh] flex-1 md:min-h-min space-y-4">
				<div className="flex flex-col justify-between gap-4 md:flex-row">
					<div>
						<h1 className="text-lg font-bold tracking-tight uppercase">
							{t("page_title")}
						</h1>

						<p className="w-full text-sm text-muted-foreground text-pretty md:w-10/12 xl:w-8/12">
							{t("page_description")}
						</p>
					</div>
				</div>

				<Separator className="bg-border" />

				<Alert>
					<TriangleAlertIcon className="size-4 shrink-0" />
					<AlertTitle className="leading-normal text-pretty">
						{t("warning")}
					</AlertTitle>
				</Alert>

				{unbackedReferences.length ? (
					<Alert variant="warning">
						<PlugZapIcon className="size-4 shrink-0" />
						<AlertTitle className="leading-normal text-pretty">
							{t("code_reference.alert_title", {
								count: unbackedReferences.length,
							})}
						</AlertTitle>
						<AlertDescription className="mt-2 space-y-3">
							{unbackedReferences.map((reference) => (
								<div
									key={`${reference.groupKey}/${reference.contentKey}`}
									className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between"
								>
									<div className="space-y-0.5">
										<p className="font-mono text-xs font-semibold">
											{reference.groupKey} / {reference.contentKey}
										</p>
										<p className="text-xs text-pretty">{reference.purpose}</p>
										<p className="text-xs text-pretty">
											{t(
												reference.status === "MISSING"
													? "code_reference.missing"
													: "code_reference.inactive",
												{
													usedBy: reference.usedBy,
													fallback: reference.fallback,
												},
											)}
										</p>

										{/* Without this line the warning contradicts what the
										    admin can see: a row spelling out the same two words
										    is sitting in the table below, looking correct. */}
										{reference.transposedRow ? (
											<p className="text-xs font-medium text-pretty">
												{t("code_reference.transposed", {
													group: reference.transposedRow.groupKey,
													key: reference.transposedRow.contentKey,
												})}{" "}
												{/* The repair makes this value live, so it belongs on
												    screen before the button is pressed — withheld, and
												    so omitted, when the reversed row is a credential. */}
												{reference.transposedRow.contentValue
													? t("code_reference.transposed_value", {
															value: reference.transposedRow.contentValue,
														})
													: null}
											</p>
										) : null}
									</div>

									{/* A reversed row is repaired, not duplicated: creating the
									    correctly named row would leave this one behind, active
									    and carrying the same two words, so the next admin could
									    not tell which of the two the code reads. */}
									{reference.transposedRow ? (
										<Button
											type="button"
											variant="outline"
											size="sm"
											className="w-fit shrink-0"
											onClick={() =>
												handleRepairKeys(reference.transposedRow?.id ?? "")
											}
										>
											<ArrowLeftRightIcon className="size-4" />
											{t("code_reference.repair_button")}
										</Button>
									) : reference.status === "MISSING" ? (
										<Button
											type="button"
											variant="outline"
											size="sm"
											className="w-fit shrink-0"
											onClick={() =>
												setFormDialog({
													mode: "create",
													content: null,
													prefill: {
														groupKey: reference.groupKey,
														contentKey: reference.contentKey,
													},
												})
											}
										>
											<PlusIcon className="size-4" />
											{t("code_reference.create_button")}
										</Button>
									) : null}
								</div>
							))}
						</AlertDescription>
					</Alert>
				) : null}

				<div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
					<div className="flex flex-col gap-2 md:flex-row md:items-center">
						{/* The icon goes through `Input`'s own StartIcon slot, not as a
					    sibling of it: `Input` wraps its field in a positioned,
					    full-width div of its own, which paints over anything placed
					    beside it — the icon was in the DOM and invisible behind the
					    field's background. */}
						<div className="w-full md:w-64">
							<Input
								value={searchTerm}
								onChange={(event) => setSearchTerm(event.target.value)}
								placeholder={t("filter.search_placeholder")}
								aria-label={t("filter.search_label")}
								StartIcon={{ icon: SearchIcon }}
							/>
						</div>

						<Select
							value={currentGroupKey || ALL_OPTION}
							onValueChange={handleGroupKeyChange}
						>
							<SelectTrigger className="w-full md:w-56">
								<SelectValue placeholder={t("filter.all_groups")} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value={ALL_OPTION}>
									{t("filter.all_groups")}
								</SelectItem>
								{filterOptions?.groupKeys?.map((groupKey) => (
									<SelectItem key={groupKey} value={groupKey}>
										{groupKey}
									</SelectItem>
								))}
							</SelectContent>
						</Select>

						<Select
							value={currentStatus || ALL_OPTION}
							onValueChange={handleStatusChange}
						>
							<SelectTrigger className="w-full md:w-40">
								<SelectValue placeholder={t("filter.all_statuses")} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value={ALL_OPTION}>
									{t("filter.all_statuses")}
								</SelectItem>
								{filterOptions?.statuses?.map((status) => (
									<SelectItem key={status} value={status}>
										{t(`status.${status}`, { defaultValue: status })}
									</SelectItem>
								))}
							</SelectContent>
						</Select>

						{hasActiveFilters ? (
							<Button
								type="button"
								variant="ghost-destructive"
								size="sm"
								onClick={handleClearFilters}
							>
								<XIcon className="size-4" />
								{t("button.clear_filters")}
							</Button>
						) : null}
					</div>

					<Button
						className="w-full md:w-fit"
						onClick={() => setFormDialog({ mode: "create", content: null })}
					>
						<PlusIcon />
						{t("button.add_data")}
					</Button>
				</div>

				<Deferred
					data={["contents"]}
					fallback={
						<div className="space-y-4">
							<Skeleton className="w-2/12 h-4 rounded-sm" />
							<Skeleton className="relative w-full h-32 rounded-xl" />
						</div>
					}
				>
					<DataTable
						columns={columns}
						data={contentsData}
						// Filtering happens on the server, so an empty page is far more
						// often a search that missed than a table with nothing in it —
						// and the two need opposite advice. The default "add the data
						// first" told an admin looking at a filtered view to start
						// creating rows that are sitting there behind the filter.
						emptyState={
							hasActiveFilters ? (
								<div className="flex flex-col items-center justify-center gap-2 text-muted-foreground">
									<div className="flex items-center gap-2">
										<SearchIcon className="size-5" />
										<span>{t("table.empty.filtered")}</span>
									</div>

									<Button
										type="button"
										variant="ghost-destructive"
										size="sm"
										onClick={handleClearFilters}
									>
										<XIcon className="size-4" />
										{t("button.clear_filters")}
									</Button>
								</div>
							) : (
								<div className="flex items-center justify-center gap-2 text-muted-foreground">
									<MousePointerClick className="size-6" />
									<span>{t("table.empty.none")}</span>
								</div>
							)
						}
						customPagination={() =>
							contents?.metadata ? (
								<ApptPagination
									label={t("pagination.contents")}
									metadata={contents.metadata}
									actions={{
										goToPrevpage: () => changePage("prev"),
										goToNextPage: () => changePage("next"),
										onChangeLimit: (value) => changePage("limit", value),
										goToPage: (value) => changePage("page", value),
										goToFirstPage: () => changePage("first"),
										goToLastPage: () => changePage("last"),
									}}
								/>
							) : null
						}
					/>
				</Deferred>

				{formDialog && (
					<ResponsiveDialog {...formDialogProps}>
						{/* Keyed on the revision: re-seeding from a newer one has to reach
						    the fields, and react-hook-form only reads `defaultValues` when
						    it mounts. Nothing else changes this key while the dialog is
						    open, so no remount can discard what the admin typed. */}
						<GenericContentFormFields
							key={`${formDialog.content?.id ?? "new"}:${formDialog.content?.revisionToken ?? ""}`}
							groupKeys={filterOptions?.groupKeys ?? []}
							codeReferences={codeReferences ?? []}
							filterQuery={filterQuery}
							content={formDialog.content ?? undefined}
							prefill={formDialog.prefill}
							supersededBy={supersededBy}
							onReloadLatest={reloadFormFromLatest}
							onClose={closeFormDialog}
							onSaved={handleFormSaved}
						/>
					</ResponsiveDialog>
				)}

				{valueDialog && (
					<ResponsiveDialog {...valueDialogProps}>
						<GenericContentValue
							t={t}
							state={valueDialog}
							onCopy={() => void handleCopyValue(valueDialog.content)}
						/>
					</ResponsiveDialog>
				)}

				{historyDialog && (
					<ResponsiveDialog {...historyDialogProps}>
						<GenericContentHistory
							t={t}
							locale={locale}
							tzDate={tzDate}
							state={historyDialog}
							onRetry={() => void handleOpenHistory(historyDialog.content)}
						/>
					</ResponsiveDialog>
				)}

				<AlertDialog
					open={!!pendingAction}
					onOpenChange={handleActionDialogOpenChange}
				>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>
								{pendingAction?.kind === "deactivate"
									? t("modal.deactivate.title")
									: t("modal.delete.title")}
							</AlertDialogTitle>
							<AlertDialogDescription className="text-pretty">
								{!pendingAction
									? t("modal.delete.fallback_description")
									: pendingAction.kind === "deactivate"
										? t("modal.deactivate.description", {
												group: pendingAction.content.groupKey,
												key: pendingAction.content.contentKey,
											})
										: t("modal.delete.description", {
												group: pendingAction.content.groupKey,
												key: pendingAction.content.contentKey,
											})}
							</AlertDialogDescription>
						</AlertDialogHeader>

						{/* The row keeps working from the admin's point of view — nothing
						    errors, the caller just starts using a different number. Saying
						    which number, and who reads it, is the whole point of this
						    confirmation. */}
						{pendingAction?.content.codeReference ? (
							<Alert variant="destructive">
								<TriangleAlertIcon className="size-4" />
								<AlertTitle>{t("modal.code_reference.title")}</AlertTitle>
								<AlertDescription className="text-pretty">
									{t("modal.code_reference.description", {
										usedBy: pendingAction.content.codeReference.usedBy,
										fallback: pendingAction.content.codeReference.fallback,
									})}
								</AlertDescription>
							</Alert>
						) : null}

						{/* The value is stripped from the list payload and redacted in the
						    audit line, so a deleted credential leaves no copy anywhere —
						    unlike every other row on this page, whose old value the log
						    still records in full. */}
						{needsTypedConfirmation && pendingAction ? (
							<div className="space-y-2">
								<Alert variant="destructive">
									<TriangleAlertIcon className="size-4" />
									<AlertTitle>{t("modal.delete.credential.title")}</AlertTitle>
									<AlertDescription className="text-pretty">
										{t("modal.delete.credential.description")}
									</AlertDescription>
								</Alert>

								<Label htmlFor="delete-confirmation" className="text-pretty">
									{t("modal.delete.credential.prompt", {
										key: pendingAction.content.contentKey,
									})}
								</Label>
								<Input
									id="delete-confirmation"
									autoComplete="off"
									className="font-mono"
									placeholder={pendingAction.content.contentKey}
									value={deleteConfirmation}
									onChange={(event) =>
										setDeleteConfirmation(event.target.value)
									}
								/>
							</div>
						) : null}

						<AlertDialogFooter>
							<AlertDialogCancel disabled={isActionLoading}>
								{t("button.cancel")}
							</AlertDialogCancel>
							<Button
								variant="destructive"
								disabled={isActionLoading || !isConfirmationSatisfied}
								onClick={(event) => {
									event.preventDefault();
									handleConfirmAction();
								}}
							>
								{isActionLoading ? (
									<>
										<Loader2 className="mr-2 size-4 animate-spin" />
										{pendingAction?.kind === "deactivate"
											? t("button.deactivating")
											: t("button.deleting")}
									</>
								) : pendingAction?.kind === "deactivate" ? (
									t("button.deactivate")
								) : (
									t("button.delete")
								)}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</PageContainer>
		</>
	);
}

type ValueShape = {
	/** "text" covers a scalar, prose, and JSON that no longer parses. */
	kind: "object" | "array" | "text";
	/** Members of the document — meaningless for "text". */
	entries: number;
	/** Bytes, which is what the column stores and the audit line truncates. */
	bytes: number;
	lines: number;
};

/**
 * Describes a value without trusting it to parse.
 *
 * The server's `isStructuredValue` says a row holds a JSON document, but the
 * rule behind it only guards *active* rows: an inactive row is allowed to hold
 * JSON that no longer parses, and that is precisely the row an admin opens to
 * repair. So a parse failure degrades to "text" rather than throwing inside a
 * table cell.
 */
function describeValue(value: string): ValueShape {
	const shape = {
		entries: 0,
		bytes: new TextEncoder().encode(value).length,
		lines: value.split("\n").length,
	};

	try {
		const parsed = JSON.parse(value);

		if (Array.isArray(parsed)) {
			return { ...shape, kind: "array", entries: parsed.length };
		}
		if (parsed !== null && typeof parsed === "object") {
			return { ...shape, kind: "object", entries: Object.keys(parsed).length };
		}
	} catch {
		// Not JSON, or not JSON any more. Either way it is text to display.
	}

	return { ...shape, kind: "text" };
}

/** Indentation is the information in a JSON document, so restore it. */
function prettyPrintValue(value: string) {
	try {
		return JSON.stringify(JSON.parse(value), null, 2);
	} catch {
		return value;
	}
}

/** "2.7 KB" — formatted by the browser, so `en` and `id` both read naturally. */
function formatBytes(bytes: number) {
	if (bytes < 1024) return `${bytes} B`;

	return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(bytes / 1024)} KB`;
}

type GenericContentColumnContext = {
	t: TFunction;
	locale: Locale;
	tzDate: ContextFn<Date>;
	revealedValues: Record<string, string>;
	pendingReveals: string[];
	onToggleReveal: (content: GenericContent) => void;
	onViewValue: (content: GenericContent) => void;
	onCopyKey: (content: GenericContent) => void;
	onCopyValue: (content: GenericContent) => void;
	onToggleActive: (content: GenericContent) => void;
	onEdit: (content: GenericContent) => void;
	onRepairKeys: (contentId: string) => void;
	onDelete: (content: GenericContent) => void;
	onOpenHistory: (content: GenericContent) => void;
};

function getColumns({
	t,
	locale,
	tzDate,
	revealedValues,
	pendingReveals,
	onToggleReveal,
	onViewValue,
	onCopyKey,
	onCopyValue,
	onToggleActive,
	onEdit,
	onRepairKeys,
	onDelete,
	onOpenHistory,
}: GenericContentColumnContext): ColumnDef<GenericContent>[] {
	return [
		{
			accessorKey: "groupKey",
			header: ({ column }) => (
				<DataTableColumnHeader
					column={column}
					title={t("table.header.group_key")}
				/>
			),
			cell: ({ row }) => (
				<Badge variant="accent" className="font-mono text-nowrap">
					{row.original.groupKey}
				</Badge>
			),
			enableSorting: false,
		},
		{
			accessorKey: "contentKey",
			header: ({ column }) => (
				<DataTableColumnHeader
					column={column}
					title={t("table.header.content_key")}
				/>
			),
			cell: ({ row }) => (
				<div className="flex items-center gap-2">
					<span className="font-mono font-semibold text-sm text-nowrap">
						{row.original.contentKey}
					</span>

					{/* Sits next to the key rather than after the badges, so it stays
					    attached to the string it copies whatever else the row carries. */}
					<Button
						variant="ghost"
						size="icon"
						className="size-7 opacity-60 hover:opacity-100"
						aria-label={t("button.copy_key")}
						onClick={() => onCopyKey(row.original)}
					>
						<CopyIcon className="size-3" />
					</Button>

					{/* A row a service looks up by name: renaming or deactivating it
					    changes behaviour, unlike the free-form content beside it. */}
					{row.original.codeReference ? (
						<Badge
							variant="outline"
							className="gap-1 text-nowrap"
							title={t("code_reference.badge_title")}
						>
							<PlugZapIcon className="size-3" />
							{t("code_reference.badge")}
						</Badge>
					) : null}

					{/* The opposite case: the row names a real lookup, but with its
					    group and key swapped, so nothing ever reads it. */}
					{row.original.transposedOf ? (
						<Badge
							variant="destructive"
							className="gap-1 text-nowrap"
							title={t("code_reference.transposed_badge_title", {
								group: row.original.transposedOf.groupKey,
								key: row.original.transposedOf.contentKey,
								usedBy: row.original.transposedOf.usedBy,
							})}
						>
							<ArrowLeftRightIcon className="size-3" />
							{t("code_reference.transposed_badge")}
						</Badge>
					) : null}
				</div>
			),
			enableSorting: false,
		},
		{
			accessorKey: "contentValue",
			header: ({ column }) => (
				<DataTableColumnHeader
					column={column}
					title={t("table.header.content_value")}
				/>
			),
			cell: ({ row }) => {
				const content = row.original;
				// `contentValue` is null for a sensitive row until it has been
				// fetched, so `hasValue` is what tells "hidden" from "empty".
				const value = content.isSensitive
					? (revealedValues[content.id] ?? "")
					: (content.contentValue ?? "");
				const masked = content.isSensitive && !(content.id in revealedValues);
				const isRevealing = pendingReveals.includes(content.id);
				const hasValue = content.isSensitive ? content.hasValue : !!value;
				// Nothing is described while the value is still withheld: the shape
				// of a credential is not the page's to disclose either.
				const shape = hasValue && !masked ? describeValue(value) : null;
				const isDocument = shape?.kind === "object" || shape?.kind === "array";
				// A value the cell cannot show in one line has to be readable
				// somewhere, or compacting the cell would simply hide it.
				const canView =
					!!shape && (isDocument || shape.lines > 1 || value.length > 80);

				return (
					<div className="flex items-center gap-1 max-w-md">
						{shape && isDocument ? (
							<Badge
								variant="outline"
								className="font-mono text-xs font-normal text-nowrap"
							>
								{t(
									shape.kind === "array"
										? "table.value.array"
										: "table.value.object",
									{ count: shape.entries, size: formatBytes(shape.bytes) },
								)}
							</Badge>
						) : (
							<span
								className={cn(
									"min-w-0 truncate font-mono text-xs",
									!hasValue && "text-muted-foreground italic",
								)}
							>
								{hasValue
									? masked
										? "•".repeat(24)
										: previewValue(value)
									: t("table.empty_value")}
							</span>
						)}

						<div className="flex items-center shrink-0">
							{content.isSensitive ? (
								<Button
									variant="ghost"
									size="icon"
									className="size-7 opacity-60 hover:opacity-100"
									disabled={isRevealing}
									aria-label={
										masked ? t("button.reveal_value") : t("button.hide_value")
									}
									onClick={() => onToggleReveal(content)}
								>
									{isRevealing ? (
										<Loader2 className="size-3 animate-spin" />
									) : masked ? (
										<EyeIcon className="size-3" />
									) : (
										<EyeOffIcon className="size-3" />
									)}
								</Button>
							) : null}

							{canView ? (
								<Button
									variant="ghost"
									size="icon"
									className="size-7 opacity-60 hover:opacity-100"
									aria-label={t("button.view_value")}
									onClick={() => onViewValue(content)}
								>
									<ExpandIcon className="size-3" />
								</Button>
							) : null}

							{hasValue ? (
								<Button
									variant="ghost"
									size="icon"
									className="size-7 opacity-60 hover:opacity-100"
									disabled={isRevealing}
									aria-label={t("button.copy_value")}
									onClick={() => onCopyValue(content)}
								>
									<CopyIcon className="size-3" />
								</Button>
							) : null}
						</div>
					</div>
				);
			},
			enableSorting: false,
		},
		{
			accessorKey: "isActive",
			header: ({ column }) => (
				<DataTableColumnHeader
					column={column}
					title={t("table.header.status")}
				/>
			),
			cell: ({ row }) => (
				<div className="flex items-center gap-2 text-nowrap">
					<Switch
						checked={!!row.original.isActive}
						onCheckedChange={() => onToggleActive(row.original)}
						aria-label={t("button.toggle_status", {
							key: row.original.contentKey,
						})}
					/>
					<Badge
						variant="outline"
						className={cn(
							"border rounded-full text-xs font-semibold uppercase",
							row.original.isActive
								? "bg-emerald-50 text-emerald-700 border-emerald-200"
								: "bg-rose-50 text-rose-700 border-rose-200",
						)}
					>
						{row.original.isActive ? t("status.ACTIVE") : t("status.INACTIVE")}
					</Badge>
				</div>
			),
			enableSorting: false,
		},
		{
			accessorKey: "updatedAt",
			header: ({ column }) => (
				<DataTableColumnHeader
					column={column}
					title={t("table.header.updated_at")}
				/>
			),
			meta: {
				headerClassName: "hidden md:table-cell",
				cellClassName: "hidden md:table-cell text-nowrap",
			},
			cell: ({ row }) =>
				row?.original?.updatedAt
					? format(new Date(row.original.updatedAt), "PPPp", {
							locale,
							in: tzDate,
						})
					: t("table.not_available"),
			enableSorting: false,
		},
		{
			id: "actions",
			header: () => (
				<span className="sr-only">{t("table.header.actions")}</span>
			),
			cell: ({ row }) => (
				<div className="flex justify-end">
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								variant="ghost"
								size="icon"
								className="border size-8 shadow-none"
								aria-label={t("button.open_actions")}
							>
								<MoreHorizontal className="size-4" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuLabel>{t("table.header.actions")}</DropdownMenuLabel>
							<DropdownMenuSeparator />
							<DropdownMenuItem
								onSelect={(event) => {
									event.preventDefault();
									onEdit(row.original);
								}}
							>
								<PencilIcon className="size-4" />
								{t("button.edit")}
							</DropdownMenuItem>
							{/* Only offered on a reversed row, and it is the whole fix:
							    the swap is what makes the lookup resolve. */}
							{row.original.transposedOf ? (
								<DropdownMenuItem
									onSelect={(event) => {
										event.preventDefault();
										onRepairKeys(row.original.id);
									}}
								>
									<ArrowLeftRightIcon className="size-4" />
									{t("code_reference.repair_button")}
								</DropdownMenuItem>
							) : null}
							{/* The table row shows what the setting holds now; this is the
							    only place the page says how it got that way. */}
							<DropdownMenuItem
								onSelect={(event) => {
									event.preventDefault();
									onOpenHistory(row.original);
								}}
							>
								<HistoryIcon className="size-4" />
								{t("button.history")}
							</DropdownMenuItem>
							<DropdownMenuItem
								className="text-destructive focus:text-destructive"
								onSelect={(event) => {
									event.preventDefault();
									onDelete(row.original);
								}}
							>
								<Trash2Icon className="size-4" />
								{t("button.remove")}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			),
			enableSorting: false,
		},
	];
}

/**
 * What has been done to one setting, newest first.
 *
 * This is a read of the audit trail, not of the row: entries survive the
 * deletion of what they describe, and a credential's value is stored as
 * "[REDACTED]", so nothing here can disclose a secret the list payload already
 * withholds.
 */
/**
 * Reads a stored value in full.
 *
 * The table cell can only say what a value *is*; this is where it says what it
 * holds. JSON is re-indented on the way in, because the indentation is the
 * information — and a document that no longer parses says so rather than being
 * silently displayed as though it were fine.
 */
function GenericContentValue({
	t,
	state,
	onCopy,
}: {
	t: TFunction;
	state: ValueDialogState;
	onCopy: () => void;
}) {
	const shape = describeValue(state.value);
	const broken = state.content.isStructuredValue && shape.kind === "text";

	return (
		<div className="space-y-3">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<Badge variant="outline" className="font-mono text-xs font-normal">
					{shape.kind === "text"
						? t("modal.value.meta_text", {
								count: shape.lines,
								size: formatBytes(shape.bytes),
							})
						: t(
								shape.kind === "array"
									? "modal.value.meta_array"
									: "modal.value.meta_object",
								{
									count: shape.entries,
									size: formatBytes(shape.bytes),
									lines: shape.lines,
								},
							)}
				</Badge>

				<Button type="button" variant="outline" size="sm" onClick={onCopy}>
					<CopyIcon className="size-4" />
					{t("button.copy_value")}
				</Button>
			</div>

			{/* Only reachable on an inactive row: the parse rule guards active rows,
			    so this is the state an admin has to be able to see to repair. */}
			{broken ? (
				<Alert variant="destructive">
					<TriangleAlertIcon className="size-4" />
					<AlertDescription className="text-pretty">
						{t("modal.value.unparsed")}
					</AlertDescription>
				</Alert>
			) : null}

			{/* A plain scroll container rather than ScrollArea: Radix sizes its
			    viewport with `h-full`, which resolves to `auto` under a parent that
			    only has a max-height — the viewport then grows to the full 71 lines
			    and the root clips them with no way to scroll. */}
			<div className="max-h-[50dvh] w-full overflow-auto rounded-md border bg-muted/30">
				<pre className="p-3 font-mono text-xs whitespace-pre-wrap break-words">
					{prettyPrintValue(state.value)}
				</pre>
			</div>
		</div>
	);
}

function GenericContentHistory({
	t,
	locale,
	tzDate,
	state,
	onRetry,
}: {
	t: TFunction;
	locale: Locale;
	tzDate: ContextFn<Date>;
	state: HistoryDialogState;
	onRetry: () => void;
}) {
	if (state.status === "loading") {
		return (
			<div className="space-y-2 py-2">
				<Skeleton className="w-full h-16" />
				<Skeleton className="w-full h-16" />
				<Skeleton className="w-full h-16" />
			</div>
		);
	}

	if (state.status === "error") {
		return (
			<Alert variant="destructive">
				<TriangleAlertIcon className="size-4" />
				<AlertTitle>{t("modal.history.error.title")}</AlertTitle>
				<AlertDescription className="space-y-2">
					<p className="text-pretty">{t("modal.history.error.description")}</p>
					<Button type="button" variant="outline" size="sm" onClick={onRetry}>
						<RefreshCwIcon className="size-4" />
						{t("modal.history.error.retry")}
					</Button>
				</AlertDescription>
			</Alert>
		);
	}

	if (!state.entries.length) {
		return (
			<p className="py-4 text-sm text-muted-foreground text-pretty">
				{t("modal.history.empty")}
			</p>
		);
	}

	return (
		<div className="space-y-3">
			{/* Said out loud rather than implied: a truncated list that looks
			    complete is the one thing an audit trail must not be. */}
			{state.truncated ? (
				<Alert>
					<TriangleAlertIcon className="size-4" />
					<AlertDescription className="text-pretty">
						{t("modal.history.truncated", { count: state.limit })}
					</AlertDescription>
				</Alert>
			) : null}

			<ol className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
				{state.entries.map((entry) => {
					// The keys as they were then. Showing them only when they differ
					// keeps the common case quiet and makes a rename stand out.
					const renamed =
						entry.groupKey !== state.content.groupKey ||
						entry.contentKey !== state.content.contentKey;

					return (
						<li key={entry.id} className="p-3 border rounded-md space-y-2">
							<div className="flex flex-wrap items-center gap-2">
								<Badge variant="outline" className="font-mono text-nowrap">
									{t(`modal.history.action.${entry.action}`)}
								</Badge>
								<span className="text-sm font-medium">{entry.admin}</span>
								<span className="text-xs text-muted-foreground text-nowrap ml-auto">
									{format(new Date(entry.createdAt), "PPPp", {
										locale,
										in: tzDate,
									})}
								</span>
							</div>

							{entry.isPredecessor ? (
								<p className="text-xs text-muted-foreground text-pretty">
									{t("modal.history.predecessor")}
								</p>
							) : null}

							{renamed ? (
								<p className="text-xs text-muted-foreground font-mono break-all">
									{entry.groupKey} / {entry.contentKey}
								</p>
							) : null}

							{entry.changes.length ? (
								<dl className="space-y-1 text-xs">
									{entry.changes.map((change) => (
										<div
											key={change.attribute}
											className="flex flex-wrap gap-x-2"
										>
											<dt className="font-mono font-semibold">
												{change.attribute}
											</dt>
											<dd className="font-mono break-all text-muted-foreground">
												{change.before ?? "nil"} &rarr; {change.after ?? "nil"}
											</dd>
										</div>
									))}
								</dl>
							) : null}

							{/* The reason this trail exists: nothing fails when a row stops
							    backing a lookup, the caller just quietly uses its fallback. */}
							{entry.effect ? (
								<p className="text-xs text-destructive text-pretty">
									{entry.effect}
								</p>
							) : null}
						</li>
					);
				})}
			</ol>
		</div>
	);
}

type GenericContentFormFieldsProps = {
	groupKeys: string[];
	/** Used to warn when the keys being typed name a lookup with a fixed shape. */
	codeReferences: GenericContentCodeReference[];
	filterQuery: string;
	onClose: () => void;
	/** Called only on a successful write, with the id of the row that changed. */
	onSaved: (contentId?: string) => void;
	content?: GenericContent;
	/** Create-mode seed, so an unbacked lookup is filled in with its exact keys. */
	prefill?: FormPrefill;
	/**
	 * The revision that replaced the one this form was seeded from, when another
	 * admin saved the row in the meantime. Null while the form is current.
	 */
	supersededBy?: GenericContent | null;
	/** Re-seeds every field from `supersededBy`, discarding what was typed. */
	onReloadLatest?: () => void;
};

/** Keeps a multi-line JSON blob from taking over the alert it is quoted in. */
function previewValue(value: string) {
	const collapsed = value.replace(/\s+/g, " ").trim();

	return collapsed.length > 120 ? `${collapsed.slice(0, 120)}…` : collapsed;
}

const GenericContentFormFields = memo(function GenericContentFormFields({
	groupKeys,
	codeReferences,
	filterQuery,
	onClose,
	onSaved,
	content,
	prefill,
	supersededBy,
	onReloadLatest,
}: GenericContentFormFieldsProps) {
	const { props: globalProps } = usePage<GenericContentIndexGlobalPageProps>();
	const { t } = useTranslation("generic-contents");
	const [isSubmitting, setIsSubmitting] = useState(false);
	// Server-side rejections (duplicate key pair, a value the reading service
	// cannot parse) have no client-side twin, so the only place they exist is the
	// response. The global toast shows `flash[:alert]`, which is the FIRST message
	// and disappears after a few seconds — not enough for a form whose failures
	// name a specific field and a specific format.
	const [serverErrors, setServerErrors] = useState<string[]>([]);
	const isEditMode = !!content;

	const form = useForm<FormValues>({
		defaultValues: {
			groupKey: content?.groupKey ?? prefill?.groupKey ?? "",
			contentKey: content?.contentKey ?? prefill?.contentKey ?? "",
			contentValue: content?.contentValue ?? "",
			isActive: content?.isActive ?? true,
		},
	});

	const indexRoute =
		globalProps.adminPortal?.router?.adminPortal?.genericContents?.index;

	// Watched, not read once: the keys are editable, so typing a pair the code
	// reads (or away from one) has to move the hint with them. The format itself
	// stays server-side — only its wording is shipped — so the rule cannot drift
	// between the two layers.
	const watchedGroupKey = form.watch("groupKey");
	const watchedContentKey = form.watch("contentKey");
	const valueHint = useMemo(() => {
		const reference = codeReferences.find(
			(candidate) =>
				candidate.groupKey === watchedGroupKey.trim() &&
				candidate.contentKey === watchedContentKey.trim(),
		);

		return reference?.valueHint ? reference : null;
	}, [codeReferences, watchedGroupKey, watchedContentKey]);

	// The same pair the other way round. The server refuses this write outright —
	// this only says so before the round-trip, and offers the one-click fix, so
	// the rule still lives in exactly one place.
	const reversedKeys = useMemo(() => {
		const groupKey = watchedGroupKey.trim();
		const contentKey = watchedContentKey.trim();
		if (!groupKey || !contentKey) return null;

		return (
			codeReferences.find(
				(candidate) =>
					candidate.groupKey === contentKey &&
					candidate.contentKey === groupKey,
			) ?? null
		);
	}, [codeReferences, watchedGroupKey, watchedContentKey]);

	// A row holding a JSON document keeps holding one, and the server refuses a
	// save that breaks it. `isStructuredValue` is the server's verdict on whether
	// the rule applies at all; the check itself is the browser's own JSON.parse,
	// so there is no second implementation of the rule to drift.
	//
	// Watching `isActive` too is not cosmetic: switching the row off in the same
	// edit is the documented way out, so the warning has to clear when it is.
	const watchedContentValue = form.watch("contentValue");
	const watchedIsActive = form.watch("isActive");
	const brokenStructuredValue = useMemo(() => {
		if (!content?.isStructuredValue || !watchedIsActive) return null;

		try {
			JSON.parse(watchedContentValue);
			return null;
		} catch (error) {
			return error instanceof Error ? error.message : String(error);
		}
	}, [content?.isStructuredValue, watchedIsActive, watchedContentValue]);

	// Re-indenting the typed value, when that is a thing worth offering.
	//
	// Offered on the typed text as well as the row's `isStructuredValue`: a
	// create form has no row to ask about, so a value that only becomes a
	// document while it is typed still gets the button — and on a row that
	// already holds one the button stays put instead of vanishing at the moment
	// a typo makes the text stop looking like a document, which is exactly when
	// an admin reaches for it.
	//
	// Anchoring on a leading brace is what makes "document" mean something:
	// `JSON.parse` accepts bare scalars, so "180" is valid JSON and reformatting
	// it would be noise.
	const jsonFormatting = useMemo(() => {
		const trimmed = watchedContentValue?.trim() ?? "";
		const looksLikeDocument =
			trimmed.startsWith("{") || trimmed.startsWith("[");
		if (!looksLikeDocument && !content?.isStructuredValue) return null;

		try {
			const formatted = JSON.stringify(JSON.parse(trimmed), null, 2);
			// Nothing to do when the value is already exactly what this would write.
			return { formatted, usable: formatted !== watchedContentValue };
		} catch {
			// Offered but unusable: the button stays visible so the field does not
			// shift as the value is typed, and says why it cannot run.
			return { formatted: null, usable: false };
		}
	}, [watchedContentValue, content?.isStructuredValue]);

	const prettifyValue = useCallback(() => {
		if (!jsonFormatting?.formatted) return;

		form.setValue("contentValue", jsonFormatting.formatted, {
			shouldDirty: true,
			shouldValidate: true,
		});
	}, [form, jsonFormatting]);

	// Where the other admin's version differs from the one this form was seeded
	// from. "Your save was refused" is not actionable on its own — the decision
	// is whether the edit still makes sense on top of their change, which needs
	// their change named.
	const supersededDifferences = useMemo(() => {
		if (!supersededBy || !content) return [];

		const differences: { label: string; value: string }[] = [];

		if (supersededBy.groupKey !== content.groupKey) {
			differences.push({
				label: t("fields.group_key.label"),
				value: supersededBy.groupKey,
			});
		}
		if (supersededBy.contentKey !== content.contentKey) {
			differences.push({
				label: t("fields.content_key.label"),
				value: supersededBy.contentKey,
			});
		}
		// A credential is never in the list payload, so there is nothing here to
		// compare against — `value_withheld` says so rather than claiming the
		// value did or did not change.
		if (
			!supersededBy.isSensitive &&
			(supersededBy.contentValue ?? "") !== (content.contentValue ?? "")
		) {
			differences.push({
				label: t("fields.content_value.label"),
				value: previewValue(supersededBy.contentValue ?? ""),
			});
		}
		if (!!supersededBy.isActive !== !!content.isActive) {
			differences.push({
				label: t("fields.is_active.label"),
				value: supersededBy.isActive
					? t("status.ACTIVE")
					: t("status.INACTIVE"),
			});
		}

		return differences;
	}, [supersededBy, content, t]);

	const swapKeys = useCallback(() => {
		form.setValue("groupKey", watchedContentKey, { shouldDirty: true });
		form.setValue("contentKey", watchedGroupKey, { shouldDirty: true });
	}, [form, watchedGroupKey, watchedContentKey]);

	const onSubmit = useCallback(
		(values: FormValues) => {
			if (!indexRoute) return;

			const endpoint = isEditMode
				? `${indexRoute}/${content?.id}${filterQuery}`
				: `${indexRoute}${filterQuery}`;

			// `generic_content_params` requires snake_case keys under the model root.
			// `revision_token` sits outside it: it is not an attribute to write but
			// the revision this dialog was seeded from. The form writes all four
			// fields back, so submitting it against a row another admin has changed
			// in the meantime would quietly revert them — the server refuses instead.
			const payload = {
				generic_content: {
					group_key: values.groupKey,
					content_key: values.contentKey,
					content_value: values.contentValue,
					is_active: values.isActive,
				},
				revision_token: content?.revisionToken ?? "",
			};

			// Close only on success. A validation failure comes back as a redirect
			// carrying `errors`, which Inertia routes to `onError` — leaving the
			// dialog mounted with everything the admin typed still in it.
			router[isEditMode ? "patch" : "post"](endpoint, payload, {
				only: RELOAD_ONLY,
				preserveScroll: true,
				preserveState: true,
				onStart: () => {
					setIsSubmitting(true);
					setServerErrors([]);
				},
				onSuccess: () => onSaved(content?.id),
				// `handle_failure` sends every message under `full_messages`; the
				// fallbacks cover an error bag shaped some other way rather than
				// leaving the admin with a dialog that silently did nothing.
				onError: (errors) => {
					const reported = (errors as Record<string, unknown>)?.full_messages;
					const messages = Array.isArray(reported)
						? reported.map((message) => String(message))
						: reported
							? [String(reported)]
							: Object.values(errors ?? {}).map((message) => String(message));

					setServerErrors(
						messages.length ? messages : [t("modal.form_error.fallback")],
					);
				},
				onFinish: () => setIsSubmitting(false),
			});
		},
		[
			indexRoute,
			isEditMode,
			content?.id,
			content?.revisionToken,
			filterQuery,
			onSaved,
			t,
		],
	);

	return (
		<Form {...form}>
			<form
				onSubmit={form.handleSubmit(onSubmit)}
				className="space-y-4 px-1 overflow-y-auto max-h-[70vh]"
			>
				{/* Ahead of the server errors: one of those errors is the refusal this
				    alert explains, and this is the only way out of it — the dialog
				    keeps the revision it opened with, so re-submitting is refused
				    again with the same message. */}
				{supersededBy ? (
					<Alert variant="warning">
						<TriangleAlertIcon className="size-4" />
						<AlertTitle>{t("modal.superseded.title")}</AlertTitle>
						<AlertDescription className="space-y-2">
							<p className="text-pretty">{t("modal.superseded.description")}</p>

							{supersededDifferences.length ? (
								<div className="space-y-1">
									<p className="font-medium">{t("modal.superseded.current")}</p>
									<ul className="pl-4 list-disc">
										{supersededDifferences.map((difference) => (
											<li key={difference.label}>
												{difference.label}:{" "}
												<span className="font-mono text-xs">
													{difference.value}
												</span>
											</li>
										))}
									</ul>
								</div>
							) : null}

							{supersededBy.isSensitive ? (
								<p className="text-pretty">
									{t("modal.superseded.value_withheld")}
								</p>
							) : null}

							<Button
								type="button"
								size="sm"
								variant="outline"
								onClick={onReloadLatest}
							>
								<RefreshCwIcon className="mr-2 size-4" />
								{t("modal.superseded.reload")}
							</Button>
						</AlertDescription>
					</Alert>
				) : null}

				{serverErrors.length ? (
					<Alert variant="destructive">
						<TriangleAlertIcon className="size-4" />
						<AlertTitle>{t("modal.form_error.title")}</AlertTitle>
						<AlertDescription>
							<ul className="pl-4 list-disc">
								{serverErrors.map((message) => (
									<li key={message}>{message}</li>
								))}
							</ul>
						</AlertDescription>
					</Alert>
				) : null}

				{reversedKeys ? (
					<Alert variant="destructive">
						<TriangleAlertIcon className="size-4" />
						<AlertTitle>{t("fields.reversed_keys.title")}</AlertTitle>
						<AlertDescription className="space-y-2">
							<p>
								{t("fields.reversed_keys.description", {
									usedBy: reversedKeys.usedBy,
									groupKey: reversedKeys.groupKey,
									contentKey: reversedKeys.contentKey,
								})}
							</p>
							<Button
								type="button"
								size="sm"
								variant="outline"
								onClick={swapKeys}
							>
								<ArrowLeftRightIcon className="mr-2 size-4" />
								{t("fields.reversed_keys.swap")}
							</Button>
						</AlertDescription>
					</Alert>
				) : null}

				{brokenStructuredValue ? (
					<Alert variant="destructive">
						<TriangleAlertIcon className="size-4" />
						<AlertTitle>{t("fields.structured_value.title")}</AlertTitle>
						<AlertDescription className="space-y-2">
							<p>{t("fields.structured_value.description")}</p>
							<p className="font-mono text-xs">{brokenStructuredValue}</p>
						</AlertDescription>
					</Alert>
				) : null}

				<FormField
					control={form.control}
					name="groupKey"
					rules={{ required: t("fields.group_key.required") }}
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t("fields.group_key.label")}</FormLabel>
							<FormControl>
								<Input
									placeholder={t("fields.group_key.placeholder")}
									className="font-mono"
									list="generic-content-group-keys"
									{...field}
								/>
							</FormControl>
							<datalist id="generic-content-group-keys">
								{groupKeys.map((groupKey) => (
									<option key={groupKey} value={groupKey} />
								))}
							</datalist>
							<FormDescription>
								{t("fields.group_key.description")}
							</FormDescription>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="contentKey"
					rules={{ required: t("fields.content_key.required") }}
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t("fields.content_key.label")}</FormLabel>
							<FormControl>
								<Input
									placeholder={t("fields.content_key.placeholder")}
									className="font-mono"
									{...field}
								/>
							</FormControl>
							<FormDescription>
								{t("fields.content_key.description")}
							</FormDescription>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="contentValue"
					render={({ field }) => (
						<FormItem>
							<div className="flex items-center justify-between gap-2">
								<FormLabel>{t("fields.content_value.label")}</FormLabel>

								{jsonFormatting ? (
									<Button
										type="button"
										variant="ghost"
										size="sm"
										className="h-7 gap-1 px-2 text-xs"
										disabled={!jsonFormatting.usable}
										title={
											jsonFormatting.usable
												? undefined
												: t("fields.content_value.prettify_unavailable")
										}
										onClick={prettifyValue}
									>
										<BracesIcon className="size-3" />
										{t("fields.content_value.prettify")}
									</Button>
								) : null}
							</div>
							<FormControl>
								<Textarea
									placeholder={t("fields.content_value.placeholder")}
									className="font-mono text-xs min-h-32"
									{...field}
								/>
							</FormControl>
							<FormDescription>
								{valueHint
									? t("fields.content_value.description_referenced", {
											usedBy: valueHint.usedBy,
											hint: valueHint.valueHint,
										})
									: content?.isSensitive
										? t("fields.content_value.description_sensitive")
										: t("fields.content_value.description")}
							</FormDescription>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="isActive"
					render={({ field }) => (
						<FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
							<div className="space-y-0.5">
								<FormLabel className="text-base">
									{t("fields.is_active.label")}
								</FormLabel>
								<FormDescription>
									{t("fields.is_active.description")}
								</FormDescription>
							</div>
							<FormControl>
								<Switch
									checked={field.value}
									onCheckedChange={field.onChange}
								/>
							</FormControl>
						</FormItem>
					)}
				/>

				<div className="flex flex-col-reverse gap-2 pt-4 sm:flex-row sm:justify-end">
					<Button
						type="button"
						variant="outline"
						onClick={onClose}
						disabled={isSubmitting}
					>
						{t("button.cancel")}
					</Button>
					<Button type="submit" disabled={isSubmitting}>
						{isSubmitting ? (
							<>
								<Loader2 className="mr-2 size-4 animate-spin" />
								{isEditMode ? t("button.updating") : t("button.creating")}
							</>
						) : isEditMode ? (
							t("button.update_data")
						) : (
							t("button.add_data")
						)}
					</Button>
				</div>
			</form>
		</Form>
	);
});
